import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ShoppingCart, Search, User, Menu, X } from 'lucide-react'
import { useCartStore } from '../../context/store'
import { useAuthStore } from '../../context/store'
import clsx from 'clsx'

const NAV_LINKS = [
  { label: 'Catalogue', href: '/catalogue' },
  { label: 'Collections', href: '/catalogue#collections' },
  { label: 'Custom Mat', href: '/customize', highlight: true },
]

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const location = useLocation()
  const navigate = useNavigate()
  const { itemCount, openCart } = useCartStore()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setSearchOpen(false)
  }, [location])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/catalogue?search=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery('')
    }
  }

  const count = itemCount()

  return (
    <>
      <header
        className={clsx(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-brand-dark/95 backdrop-blur-md border-b border-brand-gold/20 shadow-lg shadow-black/30'
            : 'bg-transparent'
        )}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-16 md:h-20">

            {/* ── Logo ── */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="font-serif text-2xl font-light tracking-wide">
                <span className="text-white">Matr</span>
                <span className="text-brand-gold italic">y</span>
                <span className="text-white">x</span>
                <span className="text-white">Lab</span>
              </span>
            </Link>

            {/* ── Desktop Nav ── */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={clsx(
                    'px-4 py-2 text-sm font-light transition-all duration-200',
                    link.highlight
                      ? 'border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-dark px-5'
                      : location.pathname === link.href
                      ? 'text-white'
                      : 'text-white/60 hover:text-white'
                  )}
                  style={{ letterSpacing: '0.12em', fontSize: '0.72rem', textTransform: 'uppercase' }}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* ── Actions ── */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2.5 text-white/60 hover:text-white transition-colors"
                aria-label="Search"
              >
                <Search size={18} />
              </button>
              <Link
                to="/account"
                className="p-2.5 text-white/60 hover:text-white transition-colors hidden md:flex"
                aria-label="Account"
              >
                <User size={18} className={isAuthenticated ? 'text-brand-gold' : ''} />
              </Link>
              <button
                onClick={openCart}
                className="p-2.5 text-white/60 hover:text-white transition-colors relative"
                aria-label="Cart"
              >
                <ShoppingCart size={18} />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-brand-gold text-brand-dark text-[10px] font-bold rounded-full flex items-center justify-center">
                    {count > 9 ? '9+' : count}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="p-2.5 text-white/60 hover:text-white transition-colors md:hidden"
                aria-label="Menu"
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* ── Search Bar ── */}
        {searchOpen && (
          <div className="border-t border-brand-gold/20 bg-brand-dark/95 backdrop-blur-md animate-slide-up">
            <div className="container-main py-4">
              <form onSubmit={handleSearch} className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search mouse mats, collections..."
                  className="input flex-1"
                  autoFocus
                />
                <button type="submit" className="btn-primary px-5">
                  Search
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ── Mobile Menu ── */}
        {mobileOpen && (
          <div className="md:hidden border-t border-brand-gold/20 bg-brand-dark/95 backdrop-blur-md animate-slide-up">
            <div className="container-main py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={clsx(
                    'px-4 py-3 text-sm font-light transition-all duration-200',
                    link.highlight
                      ? 'border border-brand-gold text-brand-gold text-center'
                      : 'text-white/70 hover:text-white'
                  )}
                  style={{ letterSpacing: '0.12em', fontSize: '0.72rem', textTransform: 'uppercase' }}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/account"
                className="px-4 py-3 text-sm font-light text-white/70 hover:text-white flex items-center gap-2"
              >
                <User size={15} />
                {isAuthenticated ? 'My Account' : 'Sign In'}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Spacer */}
      <div className="h-16 md:h-20" />
    </>
  )
}
